// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

export const INTAKE_V3_BASE_PATH = '/intake';

/** Fixed section labels for the step indicator, so nothing shifts as branches are added. */
export const INTAKE_V3_SECTIONS = ['Eligibility', 'Your details', 'Booking'] as const;

export interface IntakeV3Step {
  slug: string;
  title: string;
  /** Index into INTAKE_V3_SECTIONS. */
  section: number;
}

export const INTAKE_V3_STEPS: readonly IntakeV3Step[] = [
  { slug: 'eligibility', title: 'What state will you be in for your visit?', section: 0 },
  { slug: 'date-of-birth', title: "What's the patient's date of birth?", section: 0 },
  { slug: 'payment', title: 'How will you pay?', section: 0 },
  /** Insured patients only; IntakeV3Page skips it for self-pay. */
  { slug: 'coverage', title: 'Your insurance details', section: 0 },
  { slug: 'about-you', title: 'About you', section: 1 },
  { slug: 'where-you-live', title: "Where you live, and why you're here", section: 1 },
  { slug: 'pick-a-time', title: 'Pick a time', section: 2 },
  { slug: 'create-account', title: 'Create a password', section: 2 },
];

/** What IntakeV3Page passes each step's form. */
export interface IntakeV3StepProps {
  step: IntakeV3Step;
  /** Unset on the first step. */
  onBack?: () => void;
  onContinue: () => void;
}

/** Exits from the eligibility steps for patients Polaris can't see yet. Not part of the step sequence. */
export const INTAKE_V3_WAITLISTS = {
  outOfArea: 'out-of-area',
  underAge: 'under-age',
} as const;

export type IntakeV3Waitlist = (typeof INTAKE_V3_WAITLISTS)[keyof typeof INTAKE_V3_WAITLISTS];

export function getIntakeV3StepPath(step: IntakeV3Step | IntakeV3Waitlist): string {
  return `${INTAKE_V3_BASE_PATH}/${typeof step === 'string' ? step : step.slug}`;
}

/** Path to the step with this slug, or to the first step if there is none. */
export function getIntakeV3StepPathBySlug(slug: string): string {
  const step = INTAKE_V3_STEPS.find((candidate) => candidate.slug === slug);
  return getIntakeV3StepPath(step ?? INTAKE_V3_STEPS[0]);
}

export const INTAKE_V3_ENTRY_PATH = getIntakeV3StepPath(INTAKE_V3_STEPS[0]);

export function isIntakeV3Path(pathname: string): boolean {
  return pathname === INTAKE_V3_BASE_PATH || pathname.startsWith(`${INTAKE_V3_BASE_PATH}/`);
}
